import React, { useState, useEffect } from "react";
import "./QuickViewModal.css";

const QuickViewModal = ({ product, isOpen, onClose, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
 
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";

    // Reset states when modal opens with new product
    if (isOpen && product) {
      setSelectedVariantIndex(0);
      setActiveImage(0);
      setQuantity(1);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  // Get current variant
  const currentVariant =
    product.product_variants?.[selectedVariantIndex] ||
    product.product_variants?.[0];

  // Get images - support multiple images per variant or product level
  const getVariantImages = () => {
    // If variant has its own images
    if (currentVariant?.images && currentVariant.images.length > 0) {
      return currentVariant.images;
    }
    // If product has images array
    if (product?.images && product.images.length > 0) {
      return product.images;
    }
    // If single image
    if (product?.image) {
      return [product.image];
    }
    // Default placeholder
    return ["https://via.placeholder.com/400x400?text=No+Image"];
  };

  const images = getVariantImages();

  // Get prices
  const originalPrice = currentVariant?.price || 0;
  const finalPrice = currentVariant?.final_price || currentVariant?.price || 0;
  const discount = currentVariant?.discount || 0;
  const stockQuantity = currentVariant?.stock_quantity || 0;

  // Handle variant change
  const handleVariantChange = (index) => {
    setSelectedVariantIndex(index);
    setActiveImage(0); // Reset to first image of new variant
    setQuantity(1); // Reset quantity
  };

  // Handle quantity change
  const incrementQuantity = () => {
    if (quantity < stockQuantity && quantity < 20) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleQuantityInput = (e) => {
    let value = Number.parseInt(e.target.value);
    if (Number.isNaN(value)) value = 1;
    value = Math.max(1, Math.min(value, stockQuantity, 20));
    setQuantity(value);
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (stockQuantity === 0) {
      alert("Out of stock!");
      return;
    }
    onAddToCart(product);
    onClose();
  };

  return (
    <div className="qv-overlay" onClick={onClose}>
      <div className="qv-modal" onClick={(e) => e.stopPropagation()}>
        {/* CLOSE BUTTON */}
        <button className="qv-close" onClick={onClose}>
          ×
        </button>

        <div className="qv-content">
          {/* LEFT SECTION - IMAGE GALLERY WITH SLIDER */}
          <div className="qv-left">
            <img
              src={images[activeImage]}
              alt="product"
              className="qv-main-img"
            />{" "}
            <div className="qv-thumbs">
              {" "}
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="thumb"
                  className={activeImage === i ? "active" : ""}
                  onClick={() => setActiveImage(i)}
                />
              ))}{" "}
            </div>
          </div>

          {/* RIGHT SECTION - PRODUCT DETAILS */}
          <div className="qv-right">
            <h2 className="qv-title">{product.name}</h2>

            {/* PRICE SECTION */}
            <div className="qv-price">
              {discount > 0 && (
                <>
                  <span className="old-price">₹{originalPrice.toFixed(2)}</span>
                  <span className="discount-badge">{discount}% OFF</span>
                </>
              )}
              <span className="new-price">₹{finalPrice.toFixed(2)}</span>
            </div>

            {/* SAVINGS INFO */}
            {discount > 0 && (
              <div className="qv-savings">
                You save: ₹{(originalPrice - finalPrice).toFixed(2)}
              </div>
            )}

            {/* BRAND + CATEGORY */}
            <div className="qv-meta">
              {product?.brand_name && (
                <span className="brand">
                  <strong>Brand:</strong> {product.brand_name.trim()}
                </span>
              )}
              {product?.category_name && (
                <span className="category">
                  <strong>Category:</strong> {product.category_name}
                </span>
              )}
            </div>

            {/* LOCATION INFO */}
            {(product?.city_name || product?.state_name) && (
              <div className="qv-location">
                <strong>Shipping from:</strong> {product.city_name},{" "}
                {product.state_name} - {product.pincode}
              </div>
            )}

            {/* DESCRIPTION */}
            <p className="qv-desc">
              {product.description || "No description available"}
            </p>

            {/* VARIANTS SECTION */}
            {product?.product_variants?.length > 1 && (
              <div className="qv-variants">
                <h4>Select Size / Pack:</h4>
                <div className="variant-buttons">
                  {product.product_variants.map((variant, index) => (
                    <button
                      key={variant.id}
                      className={`variant-btn ${selectedVariantIndex === index ? "active" : ""}`}
                      onClick={() => handleVariantChange(index)}
                    >
                      {variant.size_value} {variant.size_unit}
                      {variant.discount > 0 && (
                        <span className="v-discount">-{variant.discount}%</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* VARIANT DETAILS */}
            {currentVariant?.variant_name && (
              <div className="qv-variant-details">
                <span>
                  <strong>Variant:</strong> {currentVariant.variant_name}
                </span>
                {currentVariant?.sku && (
                  <span>
                    <strong>SKU:</strong> {currentVariant.sku}
                  </span>
                )}
              </div>
            )}

            {/* STOCK STATUS */}
            <div className="qv-stock">
              {stockQuantity > 0 ? (
                <span className="in-stock">
                  ✓ In Stock ({stockQuantity}{" "}
                  {stockQuantity === 1 ? "item" : "items"} left)
                </span>
              ) : (
                <span className="out-stock">✗ Out of Stock</span>
              )}
            </div>

            {/* QUANTITY SELECTOR + ADD TO CART */}
            <div className="qv-cart">
              <div className="quantity-selector">
                <button
                  className="qty-btn"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1 || stockQuantity === 0}
                >
                  -
                </button>
                <input
                  type="number"
                  className="qty-input"
                  value={quantity}
                  onChange={handleQuantityInput}
                  min="1"
                  max={Math.min(stockQuantity, 20)}
                  disabled={stockQuantity === 0}
                  readOnly
                />
                <button
                  className="qty-btn"
                  onClick={incrementQuantity}
                  disabled={
                    quantity >= stockQuantity ||
                    quantity >= 20 ||
                    stockQuantity === 0
                  }
                >
                  +
                </button>
              </div>

              <button
                className={`add-to-cart-btn ${stockQuantity === 0 ? "disabled" : ""}`}
                onClick={handleAddToCart}
                disabled={stockQuantity === 0}
              >
                🛒 {stockQuantity === 0 ? "Out of Stock" : "Add to Cart"}
              </button>
            </div>

            {/* MAX QUANTITY LIMIT INFO */}
            {stockQuantity > 20 && (
              <div className="qv-limit-info">⚡ Maximum 20 items per order</div>
            )}

            {/* SOCIAL SHARE BUTTONS */}
            <div className="qv-social">
              <span>Share:</span>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="social-icon twitter"
              >
                🐦
              </a>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="social-icon facebook"
              >
                📘
              </a>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="social-icon whatsapp"
              >
                💬
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
